/**
 * 轻量请求封装（补齐仓库缺失的 lib/request.ts）
 *
 * 约定（与 services/api.ts 中的手写 fetch 保持一致）：
 * - 同源请求，不加 baseURL
 * - 自动带 Authorization: Bearer <localStorage.auth_token>
 * - 401 时清除 token 并派发 auth:logout 事件（App.tsx 监听后回到登录页）
 * - 非 2xx 抛 Error，附带 status / data，便于上层 catch 展示 detail
 */

type Params = Record<string, any> | undefined;

const buildUrl = (url: string, params?: Params): string => {
  if (!params) return url;
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.append(key, String(value));
  });
  const query = qs.toString();
  if (!query) return url;
  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
};

async function request<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any,
  params?: Params
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('auth_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (data !== undefined && data !== null) {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      body = data;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(data);
    }
  }

  const response = await fetch(buildUrl(url, params), { method, headers, body });

  if (response.status === 401) {
    localStorage.removeItem('auth_token');
    try {
      window.dispatchEvent(new Event('auth:logout'));
    } catch {
      /* ignore */
    }
  }

  const raw = await response.text();
  let payload: any = null;
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      payload = raw;
    }
  }

  if (!response.ok) {
    const detail = payload && (payload.detail ?? payload.message);
    const message =
      typeof detail === 'string'
        ? detail
        : detail
          ? JSON.stringify(detail)
          : `请求失败 (HTTP ${response.status})`;
    const error: any = new Error(message);
    error.status = response.status;
    error.data = payload;
    throw error;
  }

  return payload as T;
}

export const get = <T = any>(url: string, params?: Params): Promise<T> =>
  request<T>('GET', url, undefined, params);

export const post = <T = any>(url: string, data?: any): Promise<T> =>
  request<T>('POST', url, data);

export const put = <T = any>(url: string, data?: any): Promise<T> =>
  request<T>('PUT', url, data);

export const del = <T = any>(url: string, data?: any): Promise<T> =>
  request<T>('DELETE', url, data);

export default { get, post, put, del };
